import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4 pt-3 font-catering-body" }
const _hoisted_2 = { class: "flex items-start gap-3" }
const _hoisted_3 = {
  class: "bot-attention relative grid h-11 w-11 flex-shrink-0 place-items-center rounded-full bg-catering-ink-900 text-catering-lemon",
  "aria-hidden": "true"
}
const _hoisted_4 = { class: "m-0 pt-0.5 text-sm leading-relaxed text-catering-ink-600" }
const _hoisted_5 = { class: "m-0 grid list-none gap-2 p-0" }
const _hoisted_6 = ["disabled", "onClick"]
const _hoisted_7 = { class: "min-w-0 flex-1 text-sm leading-snug text-catering-ink-900" }
const _hoisted_8 = {
  key: 0,
  class: "m-0 text-sm text-catering-berry-600"
}
const _hoisted_9 = { class: "flex justify-end pt-1" }

import { ChatBubbleLeftRightIcon } from '@heroicons/vue/24/outline'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import GlowFrame from '@/components/catering/GlowFrame.vue'
import SwooshArrow from '@/components/catering/SwooshArrow.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import { CATERING_PLATFORM, ECOMMERCE_PLATFORM, useSetupState } from '@/composables/setupState'
import { openBotWidget } from '@/helpers/botWidgetOpen'
import api from '@/store/api'

// Named after where each answer lands, not after what it knows: the assistant is
// as much a way around the dashboard as it is a way to ask about it.

export default {
  __name: 'SetupAssistantStep',
  props: {
  data: { type: Object, default: () => ({}) },
  platform: { type: String, default: CATERING_PLATFORM },
  complete: { type: Boolean, default: false }
},
  emits: ['done'],
  setup(__props, { emit: __emit }) {

const QUESTIONS = {
  [ECOMMERCE_PLATFORM]: [
    'Where do I change my store hours?',
    'Where do I set up my discounts?',
    "What's running low on stock?"
  ],
  [CATERING_PLATFORM]: [
    'Where do I change my delivery zone?',
    'Where do I set up my fulfillment options?',
    "How do I see today's order sheet?"
  ]
}

const props = __props
const emit = __emit

const { applyStep } = useSetupState((...args) => api.call(...args))

const acknowledge = async () => {
  const response = await api.call('POST', 'setup/assistant', {}, { receive: false, redirectOnForbidden: false })
  applyStep(response.data)
}

const opening = ref(false)
const openError = ref('')

const questions = computed(() => QUESTIONS[props.platform] || QUESTIONS[CATERING_PLATFORM])

const messageFrom = (error, fallback) => {
  const errors = error?.response?.data?.errors
  return Array.isArray(errors) && errors.length ? errors.join('. ') : fallback
}

// Acknowledged before the panel opens, like every other step in the drawer: the
// merchant is about to lose sight of the badge, so it has to be right by then.
// openBotWidget owns closing the drawer behind it.
const launch = async prompt => {
  if (opening.value) return
  opening.value = true
  openError.value = ''
  try {
    await acknowledge()
    emit('done')
    openBotWidget({ prompt })
  } catch (error) {
    openError.value = messageFrom(
      error,
      'We could not tick this off just now. The assistant is still there to talk to.'
    )
  } finally {
    opening.value = false
  }
}

const open = () => launch()
// Asking one of the suggested questions is the same acknowledgement as opening
// the assistant - the merchant has met it either way.
const ask = question => launch(question)

// The bubble is pushed clear of the drawer above 900px and hidden below it, so
// the copy cannot point at one place and be right at both sizes.
const bubbleBesidePanel = ref(true)
let bubbleQuery = null
const syncBubblePosition = () => {
  bubbleBesidePanel.value = bubbleQuery ? bubbleQuery.matches : true
}

const intro = computed(() =>
  bubbleBesidePanel.value
    ? "That's the bubble pulsing just to the left of this panel - it's on every page of your dashboard."
    : 'Your assistant is the round black bubble in the bottom right corner of every page - it comes back as soon as you close this panel.'
)

onMounted(() => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
  bubbleQuery = window.matchMedia('(min-width: 900px)')
  syncBubblePosition()
  bubbleQuery.addEventListener('change', syncBubblePosition)
})

onBeforeUnmount(() => {
  if (bubbleQuery) bubbleQuery.removeEventListener('change', syncBubblePosition)
})

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, [
        _createVNode(_unref(ChatBubbleLeftRightIcon), { class: "h-5 w-5" })
      ]),
      _createElementVNode("p", _hoisted_4, [
        _createTextVNode(_toDisplayString(intro.value) + " ", 1),
        _cache[0] || (_cache[0] = _createElementVNode("span", { class: "text-catering-ink-900" }, "Try one of these:", -1))
      ])
    ]),
    _createElementVNode("ul", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(questions.value, (question) => {
        return (_openBlock(), _createElementBlock("li", { key: question }, [
          _createElementVNode("button", {
            type: "button",
            class: "flex w-full cursor-pointer items-center gap-3 rounded-catering-md border border-catering-cream-400 bg-catering-cream-100 px-4 py-3 text-left transition-colors hover:border-catering-tomato-600 hover:bg-catering-cream-200 disabled:cursor-default",
            disabled: opening.value,
            onClick: $event => (ask(question))
          }, [
            _createElementVNode("span", _hoisted_7, "“" + _toDisplayString(question) + "”", 1),
            _cache[1] || (_cache[1] = _createElementVNode("span", { class: "flex-shrink-0 text-sm font-semibold text-catering-tomato-600" }, "Try it", -1))
          ], 8, _hoisted_6)
        ]))
      }), 128))
    ]),
    (openError.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(openError.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_9, [
      _createVNode(GlowFrame, {
        subtle: "",
        "frame-class": "inline-flex rounded-full"
      }, {
        default: _withCtx(() => [
          _createVNode(NeueButton, {
            type: "accent",
            loading: opening.value,
            onClick: open
          }, {
            "icon-right": _withCtx(() => [
              _createVNode(SwooshArrow, { class: "ml-1" })
            ]),
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(__props.complete ? 'Open the assistant' : 'Try it now') + " ", 1)
            ]),
            _: 1
          }, 8, ["loading"])
        ]),
        _: 1
      })
    ]),
    _cache[2] || (_cache[2] = _createElementVNode("p", { class: "m-0 text-xs leading-relaxed text-catering-ink-500" }, " This closes the setup panel so the assistant has the screen to itself. Everything here is waiting where you left it when you come back. ", -1))
  ]))
}
}

}